import type { EnCatalogue } from './locales/en';

/**
 * The typing rules that make a missing translation a build failure rather than a
 * silent English string in production (PRD NFR-006, tech-stack section 12).
 *
 * English is the source of truth for the key set. Every other catalogue is typed
 * as `Catalogue`, so:
 *  - a missing key is a compile error;
 *  - a key that is plural in English must be plural in the other locale too;
 *  - a typo in a key name is a compile error at the call site.
 */

/** Locales shipped today. Adding one is adding a catalogue file, never code. */
export const LOCALES = ['en', 'fr'] as const;
export type Locale = (typeof LOCALES)[number];

/** English is the default and the fallback (PRD NFR-006). */
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Fallback chain per locale. A regional variant would resolve through its base
 * locale before reaching English - the chain is data, not a code path.
 */
export const FALLBACK_CHAIN: Readonly<Record<Locale, readonly Locale[]>> = {
  en: ['en'],
  fr: ['fr', 'en'],
};

/**
 * Plural categories. Intl.PluralRules picks the category; a catalogue only has to
 * supply the categories its language actually uses, which for EN and FR is
 * `one` and `other` - French counts 0 as `one`, English does not, and that
 * difference is handled by Intl rather than by us.
 */
export interface PluralForms {
  readonly zero?: string;
  readonly one: string;
  readonly two?: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
}

export type MessageValue = string | PluralForms;

/**
 * The shape any catalogue source has before it is narrowed. `createCatalog` in
 * `catalog.ts` builds app-local catalogues against this, so an app gets the same
 * typing, the same fallback and the same completeness check as the platform
 * catalogue without duplicating any of the machinery (PRD NFR-006).
 */
export type AnyCatalogue = Readonly<Record<string, MessageValue>>;

// ---------------------------------------------------------------------------
// Generic catalogue typing. Every helper below is parameterized by the source
// (English) catalogue, so the platform catalogue and any app-local catalogue
// share one implementation.
// ---------------------------------------------------------------------------

/** Pulls `{name}` placeholders out of a template literal type. */
type ExtractPlaceholders<S extends string> = S extends `${string}{${infer P}}${infer Rest}`
  ? P | ExtractPlaceholders<Rest>
  : never;

/** A sibling catalogue: the same keys, the same plural-ness, translated strings. */
export type CatalogueOf<TSource extends AnyCatalogue> = {
  readonly [K in keyof TSource]: TSource[K] extends string ? string : PluralForms;
};

/** The source template for a key - plural keys resolve through their `other` form. */
type TemplateOfIn<TSource extends AnyCatalogue, K extends keyof TSource> = TSource[K] extends string
  ? TSource[K]
  : TSource[K] extends { other: infer O extends string }
    ? O
    : never;

type IsPluralIn<TSource extends AnyCatalogue, K extends keyof TSource> = TSource[K] extends string
  ? false
  : true;

/** Placeholders a key needs, plus `count` when the key is plural. */
export type ParamKeysOf<TSource extends AnyCatalogue, K extends keyof TSource> =
  | ExtractPlaceholders<TemplateOfIn<TSource, K>>
  | (IsPluralIn<TSource, K> extends true ? 'count' : never);

export type MessageParamsOf<TSource extends AnyCatalogue, K extends keyof TSource> = Readonly<
  Record<ParamKeysOf<TSource, K>, string | number>
>;

/** Keys that need no parameters, so `t(locale, key)` is legal for exactly those. */
export type ParameterlessKeyOf<TSource extends AnyCatalogue> = {
  [K in keyof TSource]: [ParamKeysOf<TSource, K>] extends [never] ? K : never;
}[keyof TSource];

export type ParameterizedKeyOf<TSource extends AnyCatalogue> = Exclude<
  keyof TSource,
  ParameterlessKeyOf<TSource>
>;

// ---------------------------------------------------------------------------
// The platform catalogue, expressed through the generics above. English is the
// source of truth for the key set, so:
//  - a missing key is a compile error;
//  - a key that is plural in English must be plural in the other locale too;
//  - a typo in a key name is a compile error at the call site.
// ---------------------------------------------------------------------------

export type MessageKey = keyof EnCatalogue;

/** Every catalogue has the English key set, with matching plural-ness per key. */
export type Catalogue = CatalogueOf<EnCatalogue>;

export type ParamKeys<K extends MessageKey> = ParamKeysOf<EnCatalogue, K>;

export type MessageParams<K extends MessageKey> = MessageParamsOf<EnCatalogue, K>;

export type ParameterlessKey = ParameterlessKeyOf<EnCatalogue>;

export type ParameterizedKey = ParameterizedKeyOf<EnCatalogue>;
